import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, BookmarkPlus, CheckCircle, Clock, LayoutTemplate, Search, Send, XCircle } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/useToast';
import {
  createSocialPostDraft, deleteSocialPostDraft, getMyPostFolders, getSocialPostTemplates, placeMyPosts,
  updateSocialPostDraft, type SocialPostDraft, type SocialPostTemplate,
} from '@/services/marketing.service';
import { type PickedMedia } from './ZernioMediaPicker';
import { coverOf, firstVideoSrc, toPickedMediaList, toTemplateMediaList } from './socialTemplateMedia';
import { ZernioCoverPicker, type CoverSelection } from './ZernioCoverPicker';
import { ZernioMediaListPicker } from './ZernioMediaListPicker';
import { ZernioAudioPicker } from './ZernioAudioPicker';
import type { ZernioPost } from '@/services/zernio.service';
import {
  listZernioAccounts,
  createZernioPost,
  updateZernioPost,
  platformLabel,
  mediaSelectionError,
  availablePostTypes,
  contentTypeFor,
  postTypesFor,
  supportsCatalogAudio,
  type ZernioAudioConfiguration,
  type PostType,
  friendlyPlatformError,
  isTwitter,
  isReddit,
  requiresMedia,
  TWITTER_LIMIT,
  type ZernioAccount,
  type ZernioPostPlatform,
  type CreatePostInput,
} from '@/services/zernio.service';
import { DateTimePicker, nowAsDateTimeValue, splitDateTimeValue, toDateTimeValue } from '@/components/ui/DateTimePicker';

/**
 * What the one main button will do. `later` is "Save for later": kept in
 * Prospera, nothing posted or scheduled — only offered where saving for later
 * is (not when editing a live post, nor when opened from a template).
 */
type Mode = 'now' | 'schedule' | 'later';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onPublished?: () => void;
  initialContent?: string;
  initialMedia?: PickedMedia[];
  /** A template's saved video cover. Ignored when editing an existing post. */
  initialCover?: CoverSelection | null;
  /** Dialog title for a new post, e.g. when it starts from a template. */
  title?: string;
  /**
   * Offer "Save for later": keeps the post in Prospera, unscheduled, at the top
   * of My Posts. Off when the post was started from a template (Use template),
   * where the template itself is already the saved copy.
   */
  allowSaveForLater?: boolean;
  /**
   * A post previously saved for later, to finish. Seeds every field exactly as
   * it was left; saving again updates it, and publishing or scheduling it
   * deletes the saved copy.
   */
  draft?: SocialPostDraft | null;
  /**
   * The My Posts folder the post starts in (0 = General): the folder the person
   * is in, or a saved post's own folder. Changeable with the Folder dropdown.
   */
  folderId?: number;
  /**
   * Offer "Start from a template". Off when the post was already started from
   * one, where the content is that template's.
   */
  allowStartFromTemplate?: boolean;
  /**
   * An existing Zernio post to edit rather than create.
   *
   * The dialog seeds itself from the post and PUTs the changes instead of
   * POSTing a new one. Zernio owns the post — we keep no local row — so on
   * success the caller just refetches.
   */
  editingPost?: ZernioPost | null;
}

function describeApiError(e: any): string {
  const data = e?.response?.data;
  const details = data?.error?.details;
  if (details && typeof details === 'object') {
    const first = Object.values(details)[0];
    if (Array.isArray(first) && typeof first[0] === 'string') return first[0];
  }
  return data?.error?.message ?? e?.message ?? 'Request failed';
}

const browserTimezone = () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

export function ZernioPostComposer({
  isOpen,
  onClose,
  onPublished,
  initialContent,
  initialMedia,
  initialCover = null,
  title,
  allowSaveForLater = true,
  draft = null,
  folderId = 0,
  allowStartFromTemplate = true,
  editingPost = null,
}: Props) {
  const isEditing = editingPost !== null;
  const [content, setContent] = useState('');
  const [media, setMedia] = useState<PickedMedia[]>([]);
  const [cover, setCover] = useState<CoverSelection | null>(null);
  const [mediaUploading, setMediaUploading] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [subreddits, setSubreddits] = useState<Record<string, string>>({});
  const [postType, setPostType] = useState<PostType>('feed');
  const [audio, setAudio] = useState<ZernioAudioConfiguration | null>(null);
  const [mode, setMode] = useState<Mode>('now');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [timezone, setTimezone] = useState(browserTimezone());
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<ZernioPostPlatform[] | null>(null);
  const [postFolderId, setPostFolderId] = useState(0);
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
  const [templateQuery, setTemplateQuery] = useState('');
  const [appliedTemplate, setAppliedTemplate] = useState<SocialPostTemplate | null>(null);
  const toast = useToast();
  const qc = useQueryClient();

  // Accounts are only worth fetching once the composer is actually open.
  const { data, isLoading } = useQuery({
    queryKey: ['zernio', 'accounts'],
    queryFn: listZernioAccounts,
    enabled: isOpen,
  });


  const accounts: ZernioAccount[] = data?.accounts ?? [];

  const { data: myFolders } = useQuery({
    queryKey: ['marketing', 'my-post-folders'],
    queryFn: getMyPostFolders,
    enabled: isOpen && !isEditing,
  });

  /** File the post in the chosen folder. General needs no record unless moving a saved post back. */
  const fileInFolder = (type: 'zernio' | 'draft', key: string, always = false) => {
    if (!always && postFolderId === 0) return;
    placeMyPosts([{ type, key }], postFolderId)
      .catch(() => toast.error('The post was saved, but could not be put in its folder.'))
      .finally(() => qc.invalidateQueries({ queryKey: ['marketing', 'my-post-folders'] }));
  };

  // The template library — this account's own plus corporate's shared ones.
  // Same query key as the Templates tab, so an open tab's list is reused.
  const templatesEnabled = isOpen && allowStartFromTemplate && !isEditing;
  const { data: templates, isLoading: templatesLoading } = useQuery({
    queryKey: ['marketing', 'social-posts'],
    queryFn: () => getSocialPostTemplates(),
    enabled: templatesEnabled && templatePickerOpen,
  });
  const tq = templateQuery.trim().toLowerCase();
  const matchingTemplates = (templates ?? []).filter(
    (t) => !tq || t.caption.toLowerCase().includes(tq),
  );

  /**
   * Fill the post from a template. A copy, never a link: the template is left
   * exactly as it was. Accounts and timing are untouched — a template has no
   * opinion about where or when. Confirmed first when something is written,
   * because this replaces the caption and media outright.
   */
  const applyTemplate = (t: SocialPostTemplate) => {
    const written = content.trim() !== '' || media.length > 0;
    if (written && !window.confirm('Replace this post\'s caption and media with the template?')) return;
    setContent(t.caption);
    setMedia(toPickedMediaList(t.mediaItems));
    setCover(coverOf(t));
    setAppliedTemplate(t);
    setTemplatePickerOpen(false);
    setTemplateQuery('');
  };

  // Every open starts clean — stale selections or a stale schedule silently
  // re-publishing would be the worst possible bug here.
  useEffect(() => {
    if (!isOpen) return;
    // A template's caption and media seed the form; everything below still
    // resets, so no selection or schedule can survive from a previous open.
    setMediaUploading(false);
    setSubreddits({});
    setPostType('feed');
    setAudio(null);
    setError(null);
    setResults(null);
    setTemplatePickerOpen(false);
    setTemplateQuery('');
    setAppliedTemplate(null);
    setPostFolderId(folderId);

    if (editingPost) {
      // Editing: seed every field from the post as Zernio currently holds it, so
      // an untouched field is resent unchanged rather than cleared.
      setContent(editingPost.content ?? '');
      setMedia(
        (editingPost.mediaItems ?? [])
          .filter((m) => m.url)
          .map((m) => ({
            url: m.url as string,
            type: m.type === 'video' ? 'video' : 'image',
            name: (m.url as string).split('/').pop() || 'media',
            bytes: 0,
          })),
      );
      // Zernio does not return a post's cover, so there is nothing to seed.
      setCover(null);
      setSelected(editingPost.platforms.map((p) => p.accountId ?? '').filter(Boolean));
      // A scheduled post is always edited on the schedule tab — 'now' would mean
      // publishing it immediately, which is not what an edit is for.
      setMode('schedule');
      const when = editingPost.scheduledFor ? new Date(editingPost.scheduledFor) : null;
      // Local wall-clock, not toISOString(): that is the UTC clock and shifted the
      // shown time by the viewer's offset.
      const whenLocal = when ? toDateTimeValue(when) : '';
      setDate(whenLocal.slice(0, 10));
      setTime(whenLocal.slice(11, 16));
      setTimezone(editingPost.timezone || browserTimezone());
      return;
    }

    if (draft) {
      // Everything as it was left. The accounts are re-checked against the ones
      // connected now at submit (a removed account is simply dropped), and the
      // time is only a suggestion — nothing was ever scheduled.
      setContent(draft.caption);
      setMedia(toPickedMediaList(draft.mediaItems));
      setCover(draft.coverImageUrl ? { imageUrl: draft.coverImageUrl }
        : draft.coverOffsetMs !== null ? { offsetMs: draft.coverOffsetMs } : null);
      setSelected(draft.zernioAccountIds);
      setPostType(draft.postType);
      // Reopened on Save for later: finishing it is a deliberate switch to
      // Publish now or Schedule, never an accident of the default.
      setMode('later');
      setDate(draft.scheduledFor?.slice(0, 10) ?? '');
      setTime(draft.scheduledFor?.slice(11, 16) ?? '');
      setTimezone(draft.timezone || browserTimezone());
      return;
    }

    setContent(initialContent ?? '');
    setMedia(initialMedia ?? []);
    setCover(initialCover);
    setSelected([]);
    setMode(allowSaveForLater ? 'later' : 'now');
    setDate('');
    setTime('');
    setTimezone(browserTimezone());
  }, [isOpen]);

  const createMut = useMutation({
    mutationFn: async (input: CreatePostInput) => {
      if (editingPost) {
        // Zernio treats an absent key as "leave it alone", but the dialog always
        // has every field on screen, so send them all — what the user sees is
        // what gets saved. `mediaUrls: []` deliberately clears the media.
        await updateZernioPost(editingPost.postId, {
          content: input.content,
          platforms: input.platforms,
          mediaUrls: input.mediaUrls ?? [],
          ...(input.scheduledFor ? { scheduledFor: input.scheduledFor, timezone: input.timezone } : {}),
        });
        // An edit returns the post, not a publish result — there are no
        // per-platform outcomes to show, so close and let the caller refetch.
        return null;
      }
      return createZernioPost(input);
    },
    onSuccess: (res) => {
      if (!res) {
        onPublished?.();
        toast.success('Post updated');
        onClose();
        return;
      }
      // The UI shows a sanitised message per platform, so keep the network's own
      // wording here — it is the only place left to see why a publish failed.
      res.platforms
        .filter((p) => p.status === 'failed' && p.error)
        .forEach((p) => console.warn('[Zernio] platform failed', p.platform, p.error));
      onPublished?.();
      if (res.postId) fileInFolder('zernio', res.postId);

      // Succeeded: close, and say what happened in a toast — the dialog has
      // nothing left to do, and leaving it open invites a second submit of a
      // post that already went out.
      //
      // Asked to publish now and got back a scheduled post: the API defers a
      // video to Zernio's scheduler, because publishing one inline cannot fit
      // the request budget. The toast carries the API's own message saying when
      // it will go out, so "published" is never claimed for a queued video.
      if (res.success) {
        // It went out, so the saved-for-later copy has done its job.
        if (draft) {
          deleteSocialPostDraft(draft.id)
            .catch(() => undefined)
            .finally(() => qc.invalidateQueries({ queryKey: ['marketing', 'social-post-drafts'] }));
        }
        if (mode === 'now' && res.status === 'scheduled') {
          toast.success('Post queued', res.message);
        } else {
          toast.success(mode === 'now' ? 'Post published' : 'Post scheduled');
        }
        onClose();
        return;
      }

      // Failed or partial: stay open, because the per-platform panel is the only
      // place that says WHICH destination refused, and why.
      setResults(res.platforms);
      setError(res.message);
    },
    onError: (e: any) => {
      setResults(null);
      setError(describeApiError(e));
    },
  });

  const hasVideo = media.some((m) => m.type === 'video');
  // A cover only means something beside a video — same guard as submit().
  const templateCover = hasVideo ? cover : null;

  /**
   * Keep what is on screen as a post to finish later, without publishing.
   *
   * Everything is kept — caption, media, thumbnail, ticked accounts, post type
   * and any time typed — in Prospera, because Zernio holds no unscheduled post.
   * It appears first in My Posts marked "To be scheduled later". Nothing is
   * scheduled: the time is just remembered. Saving a post that was already
   * saved updates it rather than making a second one.
   */
  const saveTemplateMut = useMutation({
    mutationFn: () => {
      const input = {
        caption: content.trim(),
        mediaItems: toTemplateMediaList(media),
        coverImageUrl: templateCover?.imageUrl ?? null,
        coverOffsetMs: templateCover?.imageUrl ? null : templateCover?.offsetMs ?? null,
        zernioAccountIds: selected,
        postType,
        // A time typed under Schedule or Save for later is remembered, never used.
        scheduledFor: mode !== 'now' && date && time ? `${date}T${time}` : null,
        timezone: mode !== 'now' && date && time ? timezone : null,
      };
      return draft ? updateSocialPostDraft(draft.id, input) : createSocialPostDraft(input);
    },
    onSuccess: (saved) => {
      // Always recorded for a saved post, so moving one back to General sticks.
      if (saved?.id) fileInFolder('draft', String(saved.id), true);
      qc.invalidateQueries({ queryKey: ['marketing', 'social-post-drafts'] });
      toast.success('Saved for later', 'It\'s at the top of My Posts, marked "To be scheduled later".');
      onClose();
    },
    onError: (e: unknown) => toast.error(describeApiError(e)),
  });

  const canSaveTemplate =
    !isEditing &&
    content.trim() !== '' &&
    !mediaUploading &&
    !saveTemplateMut.isPending &&
    !createMut.isPending;

  const toggle = (id: string) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const longestForTwitter = useMemo(() => {
    const twitterIds = accounts.filter((a) => isTwitter(a.platform) && selected.includes(a.id));
    if (!twitterIds.length) return null;
    return content.length;
  }, [accounts, selected, content]);

  const scheduledFor = date && time ? `${date}T${time}:00` : '';

  const selectedAccounts = accounts.filter((a) => selected.includes(a.id));
  // Zernio fails these per-platform at publish time, so block them up front.
  const missingSubreddit = selectedAccounts.some(
    (a) => isReddit(a.platform) && !(subreddits[a.id] ?? '').trim(),
  );
  const missingMedia = media.length === 0 && selectedAccounts.some((a) => requiresMedia(a.platform));
  // The per-platform item/type rules. Blocking here rather than letting Zernio
  // refuse matters most for a scheduled post, which would otherwise fail days
  // after this dialog closed.
  // The types EVERY selected account can publish — see availablePostTypes. One
  // entry means there is no choice to offer, so the selector stays hidden.
  // Every type is always on screen. Before any account is ticked all three are
  // open — the choice is checked once destinations exist; after that, a type the
  // selected accounts cannot publish is greyed out and the post goes as Feed.
  const ALL_POST_TYPES: PostType[] = ['feed', 'story', 'reel'];
  const postTypes = selectedAccounts.length > 0
    ? availablePostTypes(selectedAccounts.map((a) => a.platform))
    : ALL_POST_TYPES;
  const effectiveType: PostType = postTypes.includes(postType) ? postType : 'feed';
  // Named so the hint can say WHICH destination is the constraint, which is the
  // confusing case: Instagram alone offers Stories, Instagram plus TikTok does
  // not, and nothing on screen used to explain the difference.
  const blockingAccounts = selectedAccounts.filter((a) => postTypesFor(a.platform).length === 1);

  // The catalog is reached through one connected Instagram account — see the
  // picker. The first is used, and named, rather than adding a picker inside a
  // picker.
  const instagramAccount = selectedAccounts.find(
    (a) => (a.platform ?? '').toLowerCase() === 'instagram',
  );
  const canUseAudio = supportsCatalogAudio(
    selectedAccounts.map((a) => a.platform),
    effectiveType,
  );

  const mediaError = mediaSelectionError(
    media,
    selectedAccounts.map((a) => a.platform),
    effectiveType,
  );

  const canSubmit =
    content.trim() !== '' &&
    selectedAccounts.length > 0 &&
    !createMut.isPending &&
    // Mid-save to the library the dialog is about to close; publishing now too
    // would do both.
    !saveTemplateMut.isPending &&
    !mediaUploading &&
    !missingSubreddit &&
    !missingMedia &&
    mediaError === null &&
    (mode !== 'schedule' || (date !== '' && time !== '' && timezone !== '')) &&
    true;

  // A track that is no longer publishable must not travel with the post — the
  // API would refuse it, and silently keeping it would make the refusal baffling.
  useEffect(() => {
    if (!canUseAudio && audio !== null) setAudio(null);
  }, [canUseAudio, audio]);

  const submit = () => {
    setError(null);
    setResults(null);

    // selectedAccounts, not `selected`: a post saved for later may name an
    // account that has since been disconnected, and that one is dropped.
    const platforms = selectedAccounts.map((account) => {
      const id = account.id;
      // Reddit submissions are rejected without a target subreddit.
      const subreddit = subreddits[id]?.trim().replace(/^\/?r\//i, '') ?? '';
      // Both extras live in the same object, so they are merged rather than
      // written twice. contentTypeFor returns undefined wherever the network
      // infers the type itself — an Instagram Reel is a single video and no
      // contentType, and sending one would be rejected.
      const contentType = contentTypeFor(account.platform, effectiveType);
      const specific = {
        ...(isReddit(account.platform) && subreddit ? { subreddit } : {}),
        ...(contentType ? { contentType } : {}),
        // Instagram only, and only on the account the catalog was searched
        // through — an audioId belongs to that tenant's view of the catalog.
        ...(audio && account.id === instagramAccount?.id ? { audioConfiguration: audio } : {}),
      };

      return {
        platform: account.platform ?? '',
        accountId: account.id,
        ...(Object.keys(specific).length > 0 ? { platformSpecificData: specific } : {}),
      };
    });

    const input: CreatePostInput = {
      content: content.trim(),
      platforms,
      // Array order is what Zernio receives as mediaItems, so the order shown
      // in the picker is the order published.
      ...(media.length > 0
        ? { mediaUrls: media.map((m) => ({ type: m.type, url: m.url })) }
        : {}),
      // Only sent alongside a video. Guarded rather than trusted to the picker
      // hiding itself: removing the video after choosing a cover would otherwise
      // leave a stale cover on an image post, which every platform ignores but
      // which the API would happily store.
      ...(hasVideo && cover?.imageUrl ? { coverImageUrl: cover.imageUrl } : {}),
      ...(hasVideo && cover?.offsetMs !== undefined ? { coverOffsetMs: cover.offsetMs } : {}),
      ...(mode === 'now' ? { publishNow: true } : {}),
      ...(mode === 'schedule' ? { scheduledFor, timezone } : {}),
    };

    createMut.mutate(input);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? 'Edit scheduled post' : draft ? 'Finish saved post' : title ?? 'New social post'}
      size="lg"
      closeOnEscape={false}
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            {results ? 'Close' : 'Cancel'}
          </Button>
          {/* One main button, labelled with exactly what it will do — the
              When choice above decides which. */}
          {mode === 'later' ? (
            <Button onClick={() => saveTemplateMut.mutate()} disabled={!canSaveTemplate}>
              {saveTemplateMut.isPending ? <Spinner className="h-4 w-4 mr-1" /> : <BookmarkPlus className="h-4 w-4 mr-1" />}
              Save for later
            </Button>
          ) : (
            <Button onClick={submit} disabled={!canSubmit}>
              {createMut.isPending
                ? <Spinner className="h-4 w-4 mr-1" />
                : mode === 'schedule'
                  // The same clock as the "To be scheduled later" badge in My Posts.
                  ? <Clock className="h-4 w-4 mr-1" />
                  : <Send className="h-4 w-4 mr-1" />}
              {isEditing ? 'Save changes' : mode === 'now' ? 'Publish now' : 'Schedule post'}
            </Button>
          )}
        </div>
      }
    >
      <div className="space-y-4">
        {templatesEnabled && (
          <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
            <div className="flex items-center gap-2">
              <LayoutTemplate className="h-4 w-4 text-slate-400 shrink-0" />
              <span className="text-sm text-slate-600 flex-1">
                {appliedTemplate
                  ? <>Started from a template. The template itself is unchanged.</>
                  : 'Reuse a post from your template library instead of writing a new one.'}
              </span>
              <Button size="sm" variant="secondary" onClick={() => setTemplatePickerOpen((o) => !o)}>
                {templatePickerOpen ? 'Close' : appliedTemplate ? 'Choose another' : 'Start from a template'}
              </Button>
            </div>
            {templatePickerOpen && (
              <div className="mt-2 space-y-2">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                  <Input
                    value={templateQuery}
                    onChange={(e) => setTemplateQuery(e.target.value)}
                    placeholder="Search templates…"
                    className="pl-8 h-8 text-sm"
                    autoFocus
                  />
                </div>
                <div className="max-h-52 overflow-y-auto rounded border border-slate-200 bg-white divide-y divide-slate-100">
                  {templatesLoading ? (
                    <p className="px-2.5 py-3 text-xs text-slate-500 flex items-center gap-2"><Spinner className="h-3.5 w-3.5" /> Loading templates…</p>
                  ) : matchingTemplates.length === 0 ? (
                    <p className="px-2.5 py-3 text-xs text-slate-500">
                      {tq ? `No template matches “${templateQuery}”.` : 'No templates yet.'}
                    </p>
                  ) : matchingTemplates.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => applyTemplate(t)}
                      className="w-full text-left px-2.5 py-2 hover:bg-slate-50 focus:bg-slate-50 focus:outline-none"
                    >
                      <span className="block text-sm text-slate-800 truncate">{t.caption || '(no caption)'}</span>
                      <span className="block text-[11px] text-slate-400 mt-0.5">
                        {t.folder}
                        {t.mediaItems.length > 0 && ` · ${t.mediaItems.length} media item${t.mediaItems.length === 1 ? '' : 's'}`}
                        {t.shared && ' · Corporate'}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <Textarea
          label="Caption"
          required
          rows={5}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="What do you want to say?"
        />
        <div className="flex items-center justify-between -mt-2 text-xs">
          <span className="text-slate-400">{content.length} characters</span>
          {longestForTwitter !== null && (
            <span className={cn(longestForTwitter > TWITTER_LIMIT ? 'text-red-600 font-medium' : 'text-slate-400')}>
              Twitter/X: {longestForTwitter}/{TWITTER_LIMIT}
            </span>
          )}
        </div>

        {/*
          Post type, above the media it governs.

          Hidden unless there is a real choice: only Instagram and Facebook
          document a post type, so a selection including TikTok — or any single
          non-Meta account — leaves Feed as the only option and a one-button
          selector would be noise. The list is the INTERSECTION of the selected
          accounts, so mixing Instagram with TikTok removes Story and Reel
          rather than offering something half the destinations would reject.
        */}
        {/*
          Why there is no post type to choose.

          Shown only once accounts are selected — before that the answer is
          "pick a destination first", and a hint about Stories would be noise.
          Two different reasons, and the mixed selection is the one worth
          spelling out: adding TikTok to an Instagram post silently removes
          Story and Reel, and without this nothing said so.
        */}
        {selected.length > 0 && postTypes.length <= 1 && (
          <p className="text-xs text-slate-500">
            {blockingAccounts.length > 0 && blockingAccounts.length < selectedAccounts.length
              ? `Only a normal post is available here — ${blockingAccounts
                  .map((a) => platformLabel(a.platform))
                  .join(' and ')} ${blockingAccounts.length === 1 ? 'has' : 'have'} no Stories or Reels. Post to Instagram or Facebook on their own to use them.`
              : 'Stories and Reels are available on Instagram and Facebook.'}
          </p>
        )}

        {(
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Post type</label>
            <div className="inline-flex rounded-lg border border-slate-200 p-0.5 bg-slate-50">
              {ALL_POST_TYPES.map((type) => {
                const unavailable = !postTypes.includes(type);
                return (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setPostType(type)}
                    disabled={createMut.isPending || unavailable}
                    title={unavailable ? 'Only Instagram and Facebook take Stories and Reels — the selected accounts cannot.' : undefined}
                    className={cn(
                      'px-3 py-1.5 text-sm rounded-md capitalize transition-colors',
                      effectiveType === type
                        ? 'bg-white text-slate-900 shadow-sm font-medium'
                        : 'text-slate-600 hover:text-slate-900',
                      unavailable && 'opacity-40 cursor-not-allowed hover:text-slate-600',
                    )}
                  >
                    {type}
                  </button>
                );
              })}
            </div>
            <p className="mt-1.5 text-xs text-slate-500">
              {effectiveType === 'story'
                ? 'One image or video, 9:16. Stories disappear after 24 hours, and Facebook does not show the caption.'
                : effectiveType === 'reel'
                  ? 'A single vertical video, 9:16.'
                  : selectedAccounts.some((a) => (a.platform ?? '').toLowerCase() === 'instagram')
                    ? 'Images publish to the feed. On Instagram a single video always publishes as a Reel — it cannot be a feed video.'
                    : 'The normal timeline post.'}
            </p>
          </div>
        )}

        {/*
          Music, for an Instagram Reel and nothing else.

          Hidden rather than disabled: Zernio rejects audioConfiguration on
          Stories, images and carousels, so an option that could never publish
          should not be on screen. It appears the moment Reel is chosen with an
          Instagram account selected, and disappears again if either changes.
        */}
        {canUseAudio && instagramAccount && (
          <ZernioAudioPicker
            zernioAccountId={instagramAccount.id}
            accountLabel={instagramAccount.name ?? 'Instagram'}
            value={audio}
            onChange={setAudio}
            disabled={createMut.isPending}
          />
        )}

        {/* Uploads are the default path: a pasted share link is fetched by
            Zernio at publish time and usually turns out to be an HTML preview
            page, which fails per-platform long after this dialog closed. */}
        <ZernioMediaListPicker
          value={media}
          onChange={setMedia}
          platforms={selectedAccounts.map((a) => a.platform)}
          postType={effectiveType}
          onUploadingChange={setMediaUploading}
          disabled={createMut.isPending}
        />

        {/* Below the media, and always on screen: a thumbnail photo can be set
            before the video is added. It is only sent with a video (submit
            guards on hasVideo). */}
        <ZernioCoverPicker
          value={cover}
          onChange={setCover}
          hasVideo={media.some((m) => m.type === 'video')}
          videoUrl={firstVideoSrc(media)}
          itemCount={media.length}
          onUploadingChange={setMediaUploading}
          disabled={createMut.isPending}
        />

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-sm font-medium text-slate-700">
              Accounts {mode === 'later'
                ? <span className="text-xs font-normal text-slate-400">(optional for now)</span>
                : <span className="text-red-500">*</span>}
            </label>
            <div className="flex items-center gap-2 text-xs">
              <button
                type="button"
                className="text-blue-600 hover:underline"
                onClick={() => setSelected(accounts.map((a) => a.id))}
              >
                Select all
              </button>
              <span className="text-slate-300">|</span>
              <button
                type="button"
                className="text-blue-600 hover:underline"
                onClick={() => setSelected([])}
              >
                Clear
              </button>
            </div>
          </div>

          {isLoading ? (
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Spinner className="h-4 w-4" /> Loading accounts…
            </div>
          ) : accounts.length === 0 ? (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              No active social accounts. Connect them in the Zernio dashboard, then refresh
              platforms in Marketing Integrations.
            </div>
          ) : (
            <div className="space-y-1.5">
              {accounts.map((account) => {
                const isSelected = selected.includes(account.id);
                return (
                  <div key={account.id} className="rounded-md border border-slate-200">
                    <label className="flex items-center gap-2 px-3 py-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggle(account.id)}
                        className="rounded border-slate-300"
                      />
                      <Badge variant="info" size="sm">{platformLabel(account.platform)}</Badge>
                      <span className="text-sm text-slate-700">{account.name}</span>
                      {account.username && (
                        <span className="text-xs text-slate-400">@{account.username}</span>
                      )}
                    </label>
                    {isSelected && (
                      <div className="px-3 pb-2 space-y-2">
                        {isReddit(account.platform) && (
                          <Input
                            label="Subreddit"
                            required
                            value={subreddits[account.id] ?? ''}
                            onChange={(e) =>
                              setSubreddits((prev) => ({ ...prev, [account.id]: e.target.value }))
                            }
                            placeholder="test"
                            hint="Without a subreddit Reddit rejects the post. Omit the r/ prefix."
                          />
                        )}
                        {requiresMedia(account.platform) && !media && (
                          <div className="rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5 text-xs text-amber-800">
                            {platformLabel(account.platform)} needs an image or video — add one above.
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {!isEditing && (
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1.5 block" htmlFor="zernio-post-folder">Folder</label>
            <select
              id="zernio-post-folder"
              value={postFolderId}
              onChange={(e) => setPostFolderId(Number(e.target.value))}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            >
              <option value={0}>General</option>
              {(myFolders?.folders ?? []).map((f) => (
                <option key={f.id} value={f.id}>{f.name}</option>
              ))}
            </select>
            <p className="mt-1 text-xs text-slate-500">Where it's kept in My Posts.</p>
          </div>
        )}

        <div>
          <label className="text-sm font-medium text-slate-700 mb-1.5 block">When</label>
          <div className="flex flex-wrap gap-2">
            {((!isEditing && allowSaveForLater ? ['now', 'schedule', 'later'] : ['now', 'schedule']) as Mode[]).map((m) => {
              return (
                <button
                  key={m}
                  type="button"
                  onClick={() => setMode(m)}
                  className={cn(
                    'px-3 py-1.5 rounded-md text-sm border',
                    mode === m
                      ? 'border-blue-500 bg-blue-50 text-blue-700 font-medium'
                      : 'border-slate-200 text-slate-600 hover:bg-slate-50',
                  )}
                >
                  {m === 'now' ? 'Publish now' : m === 'schedule' ? 'Schedule' : 'Save for later'}
                </button>
              );
            })}
          </div>
        </div>

        {mode === 'later' && (
          <p className="text-xs text-slate-500 -mt-2">
            Nothing is posted or scheduled. It waits at the top of My Posts, marked “To be scheduled later”,
            with everything above — accounts and a planned time are optional.
          </p>
        )}

        {mode !== 'now' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <DateTimePicker
              id="zernio-schedule-at"
              label="Date and time"
              value={date && time ? `${date}T${time}` : ''}
              onChange={(next) => {
                const { date: nextDate, time: nextTime } = splitDateTimeValue(next);
                setDate(nextDate);
                setTime(nextTime);
              }}
              min={nowAsDateTimeValue()}
              required={mode === 'schedule'}
              wrapperClassName="md:col-span-2"
            />
            <Input
              label="Timezone"
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              required={mode === 'schedule'}
            />
          </div>
        )}

        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {results && (
          <div className="space-y-1.5">
            <div className="text-xs uppercase tracking-wide text-slate-400">Result per platform</div>
            {results.map((r, i) => {
              const failed = r.status === 'failed';
              const published = r.status === 'published';
              return (
                <div
                  key={`${r.platform}-${i}`}
                  className={cn(
                    'flex items-center gap-2 rounded-md border px-3 py-2 text-sm',
                    failed
                      ? 'border-red-200 bg-red-50 text-red-700'
                      : published
                        ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                        : 'border-amber-200 bg-amber-50 text-amber-800',
                  )}
                >
                  {failed ? <XCircle className="h-4 w-4" /> : published ? <CheckCircle className="h-4 w-4" /> : <Clock className="h-4 w-4" />}
                  <span className="font-medium">{platformLabel(r.platform)}</span>
                  <span className="text-xs">{r.status}</span>
                  {failed && (
                    // Zernio relays each network's own wording, which is written
                    // for an API integrator — the TikTok capacity error tells you
                    // to set tiktokSettings.draft. The raw text is logged below
                    // instead, so support can still see it.
                    <span className="text-xs">— {friendlyPlatformError(r.platform, r.error)}</span>
                  )}
                  {r.platformPostUrl && (
                    <a
                      href={r.platformPostUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="ml-auto text-xs underline"
                    >
                      View
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Modal>
  );
}
